import React, { useCallback, useEffect, useState } from 'react';
import { Printer, ExternalLink, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { apiGet } from '../../api';
import BrandMark from '../../components/BrandMark';

// Evidence of who runs this shop, for a payment provider that asks.
//
// Every value on this page comes from the database or the signed-in session.
// There is no registration number, no tax id, and no claim of a legal entity —
// this application holds none of those, and a verification document that
// invents them is worse than no document at all. A field the shop has not
// filled in reads "Not provided", and a notice (screen only) points the owner
// at Settings to complete it.
const BusinessVerificationPage = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    setLoadError('');
    try {
      setData(await apiGet('/api/admin/business-verification'));
    } catch (error) {
      setLoadError(error.message || 'failed');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const T = {
    title: isRTL ? 'توثيق النشاط التجاري' : 'Business Verification',
    subtitle: isRTL ? 'ملكية المتجر وبيانات النشاط' : 'Store ownership and business information',
    storeInfo: isRTL ? 'بيانات المتجر' : 'Store Information',
    storeName: isRTL ? 'اسم المتجر' : 'Store Name',
    website: isRTL ? 'موقع المتجر' : 'Store Website',
    businessType: isRTL ? 'نوع النشاط' : 'Business Type',
    category: isRTL ? 'فئة المتجر' : 'Store Category',
    status: isRTL ? 'حالة المتجر' : 'Store Status',
    active: isRTL ? 'نشط' : 'Active',
    inactive: isRTL ? 'غير نشط' : 'Inactive',
    owner: isRTL ? 'المالك / المسؤول' : 'Owner / Administrator',
    fullName: isRTL ? 'الاسم الكامل' : 'Full Name',
    email: isRTL ? 'البريد الإلكتروني' : 'Email Address',
    phone: isRTL ? 'رقم الهاتف' : 'Phone Number',
    role: isRTL ? 'الصفة' : 'Role',
    contact: isRTL ? 'بيانات التواصل الرسمية للمتجر' : 'Store Contact Information',
    whatsapp: isRTL ? 'واتساب' : 'WhatsApp',
    address: isRTL ? 'العنوان' : 'Address',
    city: isRTL ? 'المدينة' : 'City',
    country: isRTL ? 'الدولة' : 'Country',
    postal: isRTL ? 'الرمز البريدي' : 'Postal Code',
    verification: isRTL ? 'التحقق من الموقع' : 'Website Verification',
    officialSite: isRTL ? 'الموقع الرسمي للمتجر' : 'Official Store Website',
    visit: isRTL ? 'زيارة المتجر' : 'Visit Store',
    relationship: isRTL ? 'العلاقة بالمتجر' : 'Relationship to Store',
    relationshipBody: isRTL
      ? 'يتولّى المسؤول المذكور أعلاه إدارة وتشغيل متجر Auraa Luxury الإلكتروني وأنشطته في التجارة الإلكترونية.'
      : 'The account administrator listed above manages and operates the Auraa Luxury online store and its e-commerce activities.',
    summary: isRTL ? 'ملخّص التوثيق' : 'Verification Summary',
    administrator: isRTL ? 'المسؤول' : 'Administrator',
    adminEmail: isRTL ? 'بريد المسؤول' : 'Administrator Email',
    adminRole: isRTL ? 'صفة المسؤول' : 'Administrator Role',
    print: isRTL ? 'طباعة / حفظ PDF' : 'Print / Save as PDF',
    notProvided: isRTL ? 'غير مُدخل' : 'Not provided',
    generated: isRTL ? 'تاريخ إصدار المستند' : 'Document generated',
    incomplete: isRTL
      ? 'بعض الحقول غير مُدخلة في إعدادات المتجر. أكملها من «الإعدادات» ليكون المستند كاملاً قبل إرساله.'
      : 'Some fields are empty in the store settings. Complete them under Settings so this document is whole before you send it.',
    loadFailed: isRTL ? 'تعذّر تحميل بيانات التوثيق' : 'Could not load the verification data',
    retry: isRTL ? 'إعادة المحاولة' : 'Retry',
    note: isRTL
      ? 'هذا المستند بيان معلوماتي صادر عن نظام المتجر، وليس شهادة حكومية ولا تسجيلاً قانونياً.'
      : 'This document is an informational statement generated by the store system. It is not a government certification or a legal registration.',
  };

  if (loadError) {
    return (
      <div className="p-6" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="max-w-xl rounded-lg border border-[#c9a24b]/40 bg-[#fdfbf6] p-6">
          <p className="font-semibold text-[#3f2d10] mb-2">{T.loadFailed}</p>
          <p className="text-sm text-[#6b5a3a] mb-4" dir="ltr">{loadError}</p>
          <Button onClick={load} variant="outline">{T.retry}</Button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#c9a24b]" role="status" aria-label="Loading" />
      </div>
    );
  }

  const value = (v) => (v === null || v === undefined || v === ''
    ? <span className="text-[#9a8c70] italic">{T.notProvided}</span>
    : v);

  const missing = Object.values(data.contact || {}).some((v) => !v)
    || !data.administrator?.full_name;

  const generatedOn = new Date(data.generated_at);
  const generatedText = Number.isNaN(generatedOn.getTime())
    ? data.generated_at
    : generatedOn.toLocaleDateString(isRTL ? 'ar-SA-u-ca-gregory-nu-latn' : 'en-GB',
        { year: 'numeric', month: 'long', day: 'numeric' });

  const Row = ({ label, children }) => (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 py-2.5 border-b border-[#e6dcc6] last:border-b-0">
      <dt className="sm:w-56 shrink-0 text-sm font-medium text-[#8a7b5e]">{label}</dt>
      <dd className="text-[15px] text-[#2c2519] font-medium break-words min-w-0">{children}</dd>
    </div>
  );

  const Section = ({ title, children }) => (
    <section className="rounded-xl border border-[#e6dcc6] bg-white/90 shadow-sm overflow-hidden print:shadow-none print:break-inside-avoid">
      <h2 className="px-5 py-3 text-sm font-bold tracking-[0.14em] uppercase text-[#6b5a3a] bg-gradient-to-r from-[#faf6ec] to-[#f4ecdc] border-b border-[#e6dcc6]">
        {title}
      </h2>
      <dl className="px-5 py-2">{children}</dl>
    </section>
  );

  return (
    <div
      className="business-verification bg-[#fdfbf6] -m-4 sm:-m-6 p-4 sm:p-8 min-h-full"
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="business-verification"
    >
      <div className="max-w-4xl mx-auto space-y-5">

        {/* Letterhead */}
        <header className="flex items-start justify-between gap-4 pb-5 border-b-2 border-[#c9a24b]">
          <div className="flex items-center gap-3 min-w-0">
            <BrandMark className="h-14 w-14 shrink-0" />
            <div className="min-w-0">
              <div className="font-display text-2xl sm:text-3xl font-black text-[#2c2519] leading-none">
                {data.store.name}
              </div>
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#8a7b5e] mt-1">Accessories</div>
              <a
                href={data.store.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#a67c2e] hover:underline"
                dir="ltr"
              >
                {data.store.website}
              </a>
            </div>
          </div>
          <Button
            onClick={() => window.print()}
            data-testid="print-verification"
            className="no-print bg-[#2c2519] hover:bg-[#3f2d10] text-[#f6efdd] shrink-0"
          >
            <Printer className="h-4 w-4 me-2" />
            <span className="hidden sm:inline">{T.print}</span>
          </Button>
        </header>

        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#2c2519]">{T.title}</h1>
          <p className="text-[#8a7b5e] mt-1">{T.subtitle}</p>
          <p className="text-sm text-[#8a7b5e] mt-1">
            {T.generated}: <span className="font-medium text-[#2c2519]">{generatedText}</span>
          </p>
        </div>

        {missing && (
          <div className="no-print flex items-start gap-3 rounded-lg border border-[#c9a24b]/50 bg-[#faf6ec] px-4 py-3">
            <AlertCircle className="h-5 w-5 text-[#a67c2e] shrink-0 mt-0.5" />
            <p className="text-sm text-[#6b5a3a]">{T.incomplete}</p>
          </div>
        )}

        <Section title={T.storeInfo}>
          <Row label={T.storeName}>{value(data.store.name)}</Row>
          <Row label={T.website}><span dir="ltr">{value(data.store.website)}</span></Row>
          <Row label={T.businessType}>{value(data.store.business_type)}</Row>
          <Row label={T.category}>{value(data.store.category)}</Row>
          <Row label={T.status}>
            <span className="inline-flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full ${data.store.status === 'active' ? 'bg-[#c9a24b]' : 'bg-[#b9b2a4]'}`} />
              {data.store.status === 'active' ? T.active : T.inactive}
            </span>
          </Row>
        </Section>

        <Section title={T.owner}>
          <Row label={T.fullName}>{value(data.administrator.full_name)}</Row>
          <Row label={T.email}><span dir="ltr">{value(data.administrator.email)}</span></Row>
          <Row label={T.phone}><span dir="ltr">{value(data.administrator.phone)}</span></Row>
          <Row label={T.role}>{value(data.administrator.role)}</Row>
        </Section>

        <Section title={T.contact}>
          <Row label={T.email}><span dir="ltr">{value(data.contact.email)}</span></Row>
          <Row label={T.phone}><span dir="ltr">{value(data.contact.phone)}</span></Row>
          <Row label={T.whatsapp}><span dir="ltr">{value(data.contact.whatsapp)}</span></Row>
          <Row label={T.address}>{value(data.contact.address)}</Row>
          <Row label={T.city}>{value(data.contact.city)}</Row>
          <Row label={T.country}>{value(data.contact.country)}</Row>
          <Row label={T.postal}>{value(data.contact.postal_code)}</Row>
        </Section>

        <Section title={T.verification}>
          <div className="py-3 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
            <div className="min-w-0">
              <div className="text-sm font-medium text-[#8a7b5e]">{T.officialSite}</div>
              <div className="text-lg font-semibold text-[#2c2519] break-all" dir="ltr">{data.store.website}</div>
            </div>
            <a href={data.store.website} target="_blank" rel="noopener noreferrer" className="no-print shrink-0">
              <Button variant="outline" className="border-[#c9a24b] text-[#6b5a3a] hover:bg-[#faf6ec]" data-testid="visit-store">
                <ExternalLink className="h-4 w-4 me-2" />
                {T.visit}
              </Button>
            </a>
          </div>
        </Section>

        <Section title={T.relationship}>
          <p className="py-3 text-[15px] leading-relaxed text-[#2c2519]">{T.relationshipBody}</p>
        </Section>

        {/* Summary card — the block a verifier reads first */}
        <section className="rounded-xl border-2 border-[#c9a24b] bg-gradient-to-br from-[#fffdf8] to-[#f8f2e4] p-5 sm:p-6 print:break-inside-avoid">
          <h2 className="text-sm font-bold tracking-[0.14em] uppercase text-[#6b5a3a] mb-4">{T.summary}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            {[
              [isRTL ? 'المتجر' : 'Store', data.store.name],
              [T.website, data.store.website],
              [T.administrator, data.administrator.full_name],
              [T.adminEmail, data.administrator.email],
              [T.adminRole, data.administrator.role],
              [T.status, data.store.status === 'active' ? T.active : T.inactive],
            ].map(([label, v]) => (
              <div key={label} className="min-w-0">
                <div className="text-xs uppercase tracking-wider text-[#8a7b5e]">{label}</div>
                <div className="text-[15px] font-semibold text-[#2c2519] break-words" dir={/[A-Za-z@:.]/.test(String(v || '')) ? 'ltr' : undefined}>
                  {value(v)}
                </div>
              </div>
            ))}
          </div>
        </section>

        <p className="text-xs text-[#8a7b5e] leading-relaxed border-t border-[#e6dcc6] pt-4">{T.note}</p>
      </div>
    </div>
  );
};

export default BusinessVerificationPage;
